import type { PlaybackController } from '@viva/player';
import { describeProblem } from '@viva/player';
import type { RefObject } from 'react';
import { useEffect, useState } from 'react';
import { codecLabel } from '../../desktop-media';
import { audioIsDecoding, bufferAhead } from './media-element';
import type { Conversion } from './usePlaybackSession';
import { t } from '../../i18n';

interface Stats {
  engine: string;
  resolution: string;
  bufferAheadS: number;
  droppedFrames: string;
  reconnects: number;
  problems: string[];
  audio: string;
}

interface PlayerStatsProps {
  videoRef: RefObject<HTMLVideoElement | null>;
  controllerRef: RefObject<PlaybackController | null>;
  stalls: RefObject<number>;
  firstFrameMs: number | null;
  conversion: Conversion | null;
}

/** Info panel (I key): engine, picture, buffer and recent problems, sampled each second. */
export function PlayerStats({ videoRef, controllerRef, stalls, firstFrameMs, conversion }: PlayerStatsProps) {
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    const sample = () => {
      const video = videoRef.current;
      const controller = controllerRef.current;
      if (!video || !controller) return;
      const candidate = controller.metrics.candidate;
      const quality = video.getVideoPlaybackQuality?.();
      const decoding = audioIsDecoding(video);
      setStats({
        engine: !candidate ? '—' : candidate.kind === 'ts' ? 'MPEG-TS (mpegts.js)' : candidate.kind === 'hls' ? 'HLS (hls.js)' : 'native video',
        resolution: video.videoWidth ? `${video.videoWidth}×${video.videoHeight}` : '—',
        bufferAheadS: Math.round(bufferAhead(video) * 10) / 10,
        droppedFrames: quality ? `${quality.droppedVideoFrames} of ${quality.totalVideoFrames}` : '—',
        reconnects: controller.metrics.reconnects,
        audio: decoding === null ? '—' : decoding ? 'decoding' : 'no audio decoded',
        problems: controller.metrics.problems
          .slice(-4)
          .map((problem) => `${Math.round(problem.atMs / 1000)} s: ${describeProblem(problem.error)}${problem.candidate > 1 ? ` (fallback ${problem.candidate})` : ''}`),
      });
    };
    sample();
    const timer = setInterval(sample, 1000);
    return () => clearInterval(timer);
  }, [videoRef, controllerRef]);

  if (!stats) return null;
  return (
    <dl className="player__stats" aria-label={t('stats.title')}>
      <dt>{t('stats.engine')}</dt>
      <dd>{stats.engine}</dd>
      <dt>{t('stats.resolution')}</dt>
      <dd>{stats.resolution}</dd>
      <dt>{t('stats.buffered')}</dt>
      <dd>{stats.bufferAheadS} s</dd>
      <dt>{t('stats.stalls')}</dt>
      <dd>{stalls.current}</dd>
      <dt>{t('stats.dropped')}</dt>
      <dd>{stats.droppedFrames}</dd>
      <dt>{t('stats.reconnects')}</dt>
      <dd>{stats.reconnects}</dd>
      <dt>{t('player.audio')}</dt>
      <dd>{stats.audio}</dd>
      <dt>{t('stats.sound')}</dt>
      <dd>{conversion ? (conversion.codec ? t('stats.convertedFrom', { codec: codecLabel(conversion.codec) }) : t('stats.converted')) : t('stats.original')}</dd>
      <dt>{t('stats.started')}</dt>
      <dd>{firstFrameMs !== null ? `${firstFrameMs} ms` : '—'}</dd>
      <dt>{t('stats.problems')}</dt>
      <dd>{stats.problems.length === 0 ? t('stats.none') : stats.problems.map((line) => <div key={line}>{line}</div>)}</dd>
    </dl>
  );
}
